import { type AxiosResponse } from 'axios'
import type { AxiosOptions } from 'shared/api'
import { baseApi } from 'entities/Auth'
import {
  type IParamsCreateAICallingAgent,
  type IParamsEnhancePrompt,
  type IParamsGenerateGreeting,
  type IParamsGetAgentCals,
  type IParamsGetAgentCalsStatistics,
  type IParamsGetList,
  type IResponseFullAICallingAgent,
  type IResponseGetAgentCals,
  type IResponseGetAgentCalsStatistics,
  type IResponseGetAgentsCallsCount,
  type IResponseGetAgentUsage,
  type IResponseGetCallingVoices,
  type IResponseGetList,
  type IResponseGettestSession,
  type IResponseTemplate,
  type IResponseEnhancePrompt,
} from './types'

class Api {
  getList = (
    params: IParamsGetList,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseGetList>> => {
    return baseApi.get('calling-agent/auth/api/v1/calling-agents', {
      params,
      ...options,
    })
  }
  create(params: IParamsCreateAICallingAgent): Promise<AxiosResponse<IResponseFullAICallingAgent>> {
    return baseApi.post('calling-agent/auth/api/v1/calling-agents', params)
  }
  update(
    id: number,
    params: IParamsCreateAICallingAgent
  ): Promise<AxiosResponse<IResponseFullAICallingAgent>> {
    return baseApi.put(`calling-agent/auth/api/v1/calling-agents/${id}`, params)
  }
  updateStatus(
    id: number,
    status: IResponseFullAICallingAgent['status']
  ): Promise<AxiosResponse<IResponseFullAICallingAgent>> {
    return baseApi.patch(`calling-agent/auth/api/v1/calling-agents/${id}/status`, { status })
  }
  delete(id: number) {
    return baseApi.delete(`calling-agent/auth/api/v1/calling-agents/${id}`)
  }

  getTemplates(): Promise<AxiosResponse<IResponseTemplate[]>> {
    return baseApi.get('calling-agent/auth/api/v1/calling-agents/templates')
  }

  getVoices(): Promise<AxiosResponse<IResponseGetCallingVoices>> {
    return baseApi.get('calling-agent/auth/api/v1/voices')
  }
  getVoicePreview(id: string): Promise<AxiosResponse<File>> {
    return baseApi.get(`calling-agent/auth/api/v1/voices/preview/${id}`, {
      responseType: 'blob',
    })
  }
  generateGreeting(params: IParamsGenerateGreeting): Promise<AxiosResponse<File>> {
    return baseApi.post(
      'calling-agent/auth/api/v1/voices/generate-greeting',
      {
        ...params,
        output_format: 'mp3',
        sample_rate: 44100,
      },
      {
        responseType: 'blob',
      }
    )
  }

  getById(id: number): Promise<AxiosResponse<IResponseFullAICallingAgent>> {
    return baseApi.get(`calling-agent/auth/api/v1/calling-agents/${id}`)
  }

  getTestSession(id: number): Promise<AxiosResponse<IResponseGettestSession>> {
    return baseApi.post(`calling-agent/auth/api/v1/calling-agents/${id}/test-session`)
  }

  getAgentCalls(
    id: number,
    params: IParamsGetAgentCals,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseGetAgentCals>> {
    return baseApi.get(`core/voice/ai-calling-agent/${id}/calls`, { params, ...options })
  }
  getAgentsCallsCount(agent_ids: number[]): Promise<AxiosResponse<IResponseGetAgentsCallsCount>> {
    return baseApi.post('core/voice/ai-calling-agent/calls/count', {
      agent_ids,
    })
  }
  getAgentCallsStatistics(
    id: number,
    params: IParamsGetAgentCalsStatistics,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseGetAgentCalsStatistics>> {
    return baseApi.get(`core/voice/ai-calling-agent/${id}/calls/statistics`, { params, ...options })
  }
  getAgentUsage(id: number): Promise<AxiosResponse<IResponseGetAgentUsage>> {
    return baseApi.get(`core/voice/ai-calling-agent/usage/${id}`)
  }

  enhancePrompt = (
    params: IParamsEnhancePrompt,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseEnhancePrompt>> => {
    return baseApi.post('calling-agent/auth/api/v1/prompt/enhance', params, options)
  }
}

export const AiCallingApi = new Api()
